import React, { createContext, ReactNode, useContext } from 'react';
import { Platform } from 'react-native';
import { AppSnackbar } from '../services/AppSnackbar';
import { requestBluetoothPermissions } from '../screens/checkout/CheckoutScreen';
import { useAppDispatch, useAppSelector } from '../redux/helper';
import {
  selectEnabledPrinterProfiles,
  selectPrinterSettings,
} from '../redux/SettingsSlice';
import { ExtrasContext } from './ExtrasContext';
import { RootState } from '../redux/types';
import {
  DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE,
  getThermalPrintErrorMessage,
  printOrderThermalReceipt,
} from '../services/thermalReceiptService';
import {
  profileToTarget,
  profileToLayoutOpts,
} from '../utils/printerSettingsPayload';
import {
  markPrinterProfileChecking,
  markPrinterProfilePrintFailure,
  markPrinterProfilePrintSuccess,
} from '../services/printerProfileStatusService';
import { isElectronRuntime } from '../utils/isElectron';
import { shareReceiptAsPDF } from '../services/receiptShareService';
import type { ReceiptTotalsMode } from '../services/receiptTotals';

interface PrintReceiptContextType {
  btprint: (totalsMode?: ReceiptTotalsMode) => Promise<void>;
  shareReceipt: (totalsMode?: ReceiptTotalsMode) => Promise<void>;
}

export const PrintReceiptContext = createContext<PrintReceiptContextType>(
  {} as PrintReceiptContextType,
);

interface PrintReceiptContextProviderObject {
  children: ReactNode;
}

export const PrintReceiptContextProvider: React.FC<
  PrintReceiptContextProviderObject
> = ({ children }) => {
  const dispatch = useAppDispatch();
  const profiles = useAppSelector(selectEnabledPrinterProfiles);
  const legacyPrinter = useAppSelector(selectPrinterSettings);
  const { receipt } = useContext(ExtrasContext);
  const business = useAppSelector(
    (state: RootState) => state.auth.inApp.account.businessInfo,
  );
  const employeedata = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );
  const currency = useAppSelector(
    (state: RootState) =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );

  const btprint = async (
    totalsMode: ReceiptTotalsMode = 'print',
  ): Promise<void> => {
    // Fall back to legacy single-printer path if no profiles configured
    if (!profiles.length) {
      await legacyPrint(totalsMode);
      return;
    }

    // Request BLE permissions once if any profile needs Bluetooth
    // Skip on Electron — Web Bluetooth handles permissions via the device picker
    if (profiles.some(p => p.mode === 'bluetooth') && !isElectronRuntime()) {
      const hasBt = await requestBluetoothPermissions();
      if (!hasBt) {
        AppSnackbar.show('Bluetooth permission is required to print.', 'error');
        return;
      }
    }
    const payloadArgs = {
      receipt,
      business,
      employeedata,
      currency,
      totalsMode,
    };
    let ok = 0;
    const failed: Array<{ label: string; err: unknown }> = [];

    for (const profile of profiles) {
      try {
        markPrinterProfileChecking(dispatch, profile.id);
        const target = profileToTarget(profile);
        const layoutOpts = profileToLayoutOpts(profile);
        await printOrderThermalReceipt(payloadArgs, layoutOpts, target);
        markPrinterProfilePrintSuccess(dispatch, profile.id, 'print');
        ok++;
      } catch (e) {
        markPrinterProfilePrintFailure(
          dispatch,
          profile.id,
          'print',
          getThermalPrintErrorMessage(e),
        );
        failed.push({ label: profile.label, err: e });
        console.log(`Print failed for "${profile.label}":`, e);
      }
    }

    if (ok === 0) {
      const firstError = failed[0]?.err;
      AppSnackbar.show(getThermalPrintErrorMessage(firstError), 'error', {
        durationMs: 7500,
      });
    } else if (failed.length) {
      const failedNames = failed.map(f => f.label).join(', ');
      AppSnackbar.show(
        `Printed to ${ok}/${profiles.length}. Failed: ${failedNames}`,
        'warning',
        { durationMs: 7500 },
      );
    }
  };

  /**
   * Legacy single-printer path — used when no printerProfiles exist.
   * Preserves exact behavior for users who haven't migrated.
   */
  const legacyPrint = async (
    totalsMode: ReceiptTotalsMode = 'print',
  ): Promise<void> => {
    const printer = legacyPrinter;
    const charsPerLine = printer?.charsPerLine ?? 32;
    const widthMM = printer?.widthMM ?? 48;
    const autoCut = printer?.autoCut ?? false;
    const timeoutMs = printer?.timeoutMs ?? 30000;
    const ip = printer?.ip ?? '192.168.100.246';
    const port = printer?.port ?? 9100;

    const payloadArgs = {
      receipt,
      business,
      employeedata,
      currency,
      totalsMode,
    };
    const layoutOpts = { charsPerLine, autoCut, widthMM };

    if (Platform.OS === 'web') {
      try {
        if (printer?.mode === 'network') {
          const name = (printer?.printerName || '').trim();
          if (!name) {
            AppSnackbar.show(
              'No printer selected. Go to Printer Settings and select a printer.',
              'warning',
              { durationMs: 7500 },
            );
            return;
          }
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'network',
            printerName: name,
          });
          return;
        }

        if (printer?.mode === 'usb') {
          const vid = (printer?.usbVendorId || '').trim();
          const pid = (printer?.usbProductId || '').trim();
          if (!vid || !pid) {
            AppSnackbar.show(
              'No printer saved. Go to Printer Settings, scan a printer, then test or save the printer.',
              'warning',
              { durationMs: 7500 },
            );
            return;
          }
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'usb',
            vendorId: vid,
            productId: pid,
          });
          return;
        }

        if (printer?.mode === 'web-bluetooth') {
          const profile = DEFAULT_WEB_BLUETOOTH_THERMAL_PROFILE;
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'web-bluetooth' as const,
            bleDeviceId: (printer?.bleDeviceId || '').trim() || undefined,
            bleDeviceName: (printer?.bleDeviceName || '').trim() || undefined,
            bleServiceUuid:
              (printer?.bleServiceUuid || '').trim() || profile.bleServiceUuid,
            bleCharacteristicUuid:
              (printer?.bleCharacteristicUuid || '').trim() ||
              profile.bleCharacteristicUuid,
            bleNamePrefix:
              (printer?.bleNamePrefix || '').trim() ||
              (printer?.bleDeviceName || '').trim() ||
              undefined,
          });
          return;
        }

        if (printer?.mode === 'web-serial') {
          const baudRate = Number(printer?.serialBaudRate) || 38400;
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'web-serial',
            serialBaudRate: baudRate,
            serialDataBits: printer?.serialDataBits,
            serialStopBits: printer?.serialStopBits,
            serialParity: printer?.serialParity,
            serialFlowControl: printer?.serialFlowControl,
            serialBufferSize: printer?.serialBufferSize,
            serialUsbVendorId: printer?.serialUsbVendorId,
            serialUsbProductId: printer?.serialUsbProductId,
          });
          return;
        }

        AppSnackbar.show(
          'No printer configured. Choose a printer in Printer Settings first.',
          'warning',
          { durationMs: 7500 },
        );
      } catch (err) {
        console.log('Web printing failed:', err);
        AppSnackbar.show(getThermalPrintErrorMessage(err), 'error', {
          durationMs: 7500,
        });
      }
      return;
    }

    const printViaNet = async (): Promise<void> => {
      await printOrderThermalReceipt(payloadArgs, layoutOpts, {
        mode: 'tcp',
        host: ip,
        port,
        timeoutMs,
      });
    };

    const printViaBle = async (): Promise<void> => {
      const hasBt = await requestBluetoothPermissions();
      if (!hasBt) {
        AppSnackbar.show(
          'Bluetooth not available, trying network printer…',
          'warning',
          { durationMs: 2000 },
        );
        await printViaNet();
        return;
      }

      const macs = (
        printer?.bluetoothAddresses?.length
          ? printer.bluetoothAddresses
          : (printer?.bluetoothAddress || '').trim()
            ? [String(printer?.bluetoothAddress || '').trim()]
            : []
      )
        .map(s => String(s || '').trim())
        .filter(Boolean);

      let targets = macs;
      if (!targets.length) {
        throw new Error('No Bluetooth printers selected');
      }

      let bleOk = 0;
      const bleFailed: Array<{ mac: string; err: unknown }> = [];
      for (const mac of targets) {
        try {
          await printOrderThermalReceipt(payloadArgs, layoutOpts, {
            mode: 'bluetooth',
            macAddress: mac,
          });
          bleOk++;
        } catch (e) {
          bleFailed.push({ mac, err: e });
        }
      }

      if (bleOk === 0) {
        throw new Error('Bluetooth printing failed');
      }
      if (bleFailed.length) {
        console.log('Some BLE printers failed:', bleFailed);
        AppSnackbar.show(
          `Printed to ${bleOk}/${targets.length} Bluetooth printer(s).`,
          'warning',
          { durationMs: 2500 },
        );
      }
    };

    const printViaUsb = async (): Promise<void> => {
      if (Platform.OS !== 'android') {
        throw new Error('USB printing is only supported on Android');
      }
      const vid = (printer?.usbVendorId || '').trim();
      const pid = (printer?.usbProductId || '').trim();
      if (!vid || !pid) {
        throw new Error('Select a USB printer in Printer Settings');
      }
      await printOrderThermalReceipt(payloadArgs, layoutOpts, {
        mode: 'usb',
        vendorId: vid,
        productId: pid,
      });
    };

    try {
      if (printer?.mode === 'usb') {
        await printViaUsb();
        return;
      }
      if (printer?.mode === 'tcp') {
        await printViaNet();
        return;
      }

      try {
        await printViaBle();
      } catch (bleErr) {
        console.log('Bluetooth printing failed, trying network:', bleErr);
        AppSnackbar.show(
          'Bluetooth print failed, trying network printer…',
          'warning',
          { durationMs: 2500 },
        );
        await printViaNet();
      }
    } catch (err) {
      console.log('Printing failed:', err);
      AppSnackbar.show(getThermalPrintErrorMessage(err), 'error', {
        durationMs: 7500,
      });
    }
  };

  const shareReceipt = async (
    totalsMode: ReceiptTotalsMode = 'print',
  ): Promise<void> => {
    try {
      await shareReceiptAsPDF({
        receipt,
        business,
        employeedata,
        currency,
        totalsMode,
      });
    } catch (err: any) {
      // User cancelled the share sheet — not an error
      if (
        err?.message?.includes('User did not share') ||
        err?.message?.includes('CANCELED')
      ) {
        return;
      }
      console.log('Share receipt failed:', err);
      AppSnackbar.show('Failed to share receipt. Please try again.', 'error', {
        durationMs: 7500,
      });
    }
  };

  return (
    <PrintReceiptContext.Provider value={{ btprint, shareReceipt }}>
      {children}
    </PrintReceiptContext.Provider>
  );
};
